import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../store/useSessionStore";
import { usePartnerStore } from "../store/usePartnerStore";
import { useStore } from "../store/globalState";
import { RequestStatus, LINK_ERROR_CODE } from "../utils/commons";
import { isAxiosNetworkFailure } from "../utils/axiosNetworkError";
import { useOfflineStore } from "../store/useOfflineStore";
import {
  claimLink,
  dispatchSessionExpiredEvent,
  LINK_ALREADY_USED_EVENT,
  LinkClaimError,
  setWidgetLinkId,
} from "../services/api";
import {
  applyDeadLinkPartnerRecovery,
  partnerRecoveryFromErrorDetails,
} from "../utils/dead-link-recovery";
import { getDeviceSession } from "../device/seon-session";
import { waitForPersistHydration } from "../utils/quoteCommit";
import {
  buildDistinctId,
  getEvents,
  normalizeFlow,
  safeCapture,
  safeGroup,
  safeIdentify,
  maybeReportAnalyticsSession,
  safeRegister,
  setAnalyticsFlow,
} from "../analytics";

/**
 * Claims the magic link present in the URL (path param `:linkId` or `?linkId=`).
 * On success it stores the session — which carries the partner theme that
 * `useReadTheme` then applies — and configures the link id for later requests.
 */

interface UseClaimLinkOptions {
  /**
   * LoadingPage entry: always re-claim even when a LOADED session exists for
   * this linkId, so expiry/revocation is detected before splash/landing.
   * Mid-flow remounts leave this false to avoid a claim on every page.
   */
  revalidate?: boolean;
}

export function useClaimLink(options: UseClaimLinkOptions = {}) {
  const { revalidate = false } = options;
  const { linkId: linkIdFromParams } = useParams<{ linkId: string }>();
  const [searchParams] = useSearchParams();
  const linkIdFromQuery = searchParams.get("linkId");

  // Obtain linkId ONLY from the URL (path params > query params)
  const linkId = linkIdFromParams || linkIdFromQuery;

  const { session, setSession, setError, resetSession } = useSessionStore();
  const setPartnerInfo = usePartnerStore((state) => state.setPartnerInfo);
  const { setLinkId } = useStore();
  const { t, i18n } = useTranslation();
  const isLoadingRef = useRef(false);
  const revalidatedRef = useRef<string | null>(null);
  const retryClaimOnOnlineRef = useRef(false);
  const [claimAttempt, setClaimAttempt] = useState(0);

  useEffect(() => {
    const onOnline = () => {
      if (!retryClaimOnOnlineRef.current) return;
      retryClaimOnOnlineRef.current = false;
      setClaimAttempt((n) => n + 1);
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  useEffect(() => {
    if (retryClaimOnOnlineRef.current) return;

    if (!linkId) {
      setError({
        title: t("error.invalidLink"),
        subTitle: null,
        message: t("error.tokenMissing"),
        btnText: null,
        isOut: false,
        errorCode: LINK_ERROR_CODE.INVALID_TOKEN,
      });
      return;
    }

    let cancelled = false;

    void (async () => {
      // Avoid a claim-then-hydrate race: persist can overwrite a fresh claim
      // with the stale sessionStorage snapshot (including partner.theme).
      await waitForPersistHydration(useSessionStore.persist);
      if (cancelled || isLoadingRef.current) return;

      const current = useSessionStore.getState().session;

      // Session already claimed for THIS link this visit — wire APIs
      // without hitting claim again (SPA remounts). A missing partner theme
      // is not a reason to claim again: useReadTheme falls back to
      // public/theme.json, and retrying loops while the claim omits theme.
      if (
        !revalidate &&
        current.data?.linkId === linkId &&
        current.status === RequestStatus.LOADED
      ) {
        setWidgetLinkId(linkId);
        setLinkId(linkId);
        maybeReportAnalyticsSession(linkId);
        return;
      }

      // A different link (or a never-claimed one) reached this view. Drop any
      // stale persisted session before claiming the new link, otherwise the
      // previous link's data would leak.
      const isStaleSession =
        current.status === RequestStatus.LOADED &&
        current.data?.linkId !== linkId;
      if (isStaleSession) {
        usePartnerStore.getState().clearPartnerInfo();
        setWidgetLinkId(null);
        setLinkId(null);
      }

      const needsRevalidate =
        revalidate &&
        revalidatedRef.current !== linkId &&
        current.data?.linkId === linkId;

      if (
        !(
          current.status === RequestStatus.NOT_LOADED ||
          isStaleSession ||
          needsRevalidate
        )
      ) {
        return;
      }

      isLoadingRef.current = true;

      try {
          setSession({
            data: current.data,
            status: RequestStatus.LOADING,
            error: null,
          });

          const deviceInfo = {
            userAgent: navigator.userAgent,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };

          const seonSession = await getDeviceSession();
          const sessionData = await claimLink(
            linkId,
            deviceInfo,
            i18n.language,
            seonSession ?? undefined
          );

          // Persist the link id for later views and API requests
          setLinkId(sessionData.linkId);
          setWidgetLinkId(sessionData.linkId);

          // Apply the link language if configured
          if (
            sessionData.language &&
            ["en", "es", "fr"].includes(sessionData.language)
          ) {
            i18n.changeLanguage(sessionData.language);
          }

          setPartnerInfo({
            sessionExpiredRedirectUrl:
              sessionData.partner?.sessionExpiredRedirectUrl,
            name: sessionData.partner?.name,
            supportEmail: sessionData.partner?.supportEmail,
            supportWhatsappNumber: sessionData.partner?.supportWhatsappNumber,
          });

          setSession({
            data: sessionData,
            status: RequestStatus.LOADED,
            error: null,
          });

          const flow = normalizeFlow(sessionData.config?.flow);
          setAnalyticsFlow(flow);
          const events = getEvents();
          const partnerId = sessionData.partner?.partnerId;
          const referenceId = sessionData.userData?.referenceId;
          if (partnerId && referenceId) {
            const distinctId = buildDistinctId(partnerId, referenceId);
            safeIdentify(distinctId, {
              partner_id: partnerId,
              partner_name: sessionData.partner?.name,
              reference_id: referenceId,
            });
            safeGroup("partner", partnerId, {
              name: sessionData.partner?.name,
            });
          }
          safeRegister({
            link_id: sessionData.linkId,
            widget_session_id: sessionData.sessionId,
            flow: sessionData.config?.flow,
            partner_id: partnerId,
            partner_name: sessionData.partner?.name,
            flavor: sessionData.flavor,
            language: sessionData.language,
          });
          const defaultCorridor = sessionData.config?.defaultCorridor;
          safeCapture(events.LINK_OPENED, {
            success: true,
            link_id: sessionData.linkId,
            partner_id: partnerId,
            partner_name: sessionData.partner?.name,
            corridors_count: sessionData.config?.corridors?.length ?? 0,
            default_corridor: defaultCorridor
              ? `${defaultCorridor.originCountryCode}-${defaultCorridor.destinationCountryCode}`
              : undefined,
            terms_accepted: sessionData.termsAccepted,
            flow: sessionData.config?.flow,
          });
          maybeReportAnalyticsSession(sessionData.linkId);

          revalidatedRef.current = linkId;
          isLoadingRef.current = false;
        } catch (error: unknown) {
          isLoadingRef.current = false;
          if (isAxiosNetworkFailure(error)) {
            retryClaimOnOnlineRef.current = true;
            useOfflineStore.getState().reportOffline();
            setWidgetLinkId(null);
            setLinkId(null);
            setSession({
              data: null,
              status: RequestStatus.NOT_LOADED,
              error: null,
            });
            return;
          }
          revalidatedRef.current = linkId;
          if (error instanceof LinkClaimError) {
            applyDeadLinkPartnerRecovery(
              partnerRecoveryFromErrorDetails(error.details),
            );
          }
          const keepSessionForUsedLink =
            error instanceof LinkClaimError &&
            error.code === LINK_ERROR_CODE.LINK_ALREADY_USED;
          const maintenanceClaim =
            error instanceof LinkClaimError &&
            error.code === LINK_ERROR_CODE.SERVICE_UNAVAILABLE_MAINTENANCE;
          if (!keepSessionForUsedLink) {
            resetSession();
            if (maintenanceClaim) {
              setWidgetLinkId(linkId);
            } else {
              setWidgetLinkId(null);
              setLinkId(null);
            }
          }


          if (error instanceof LinkClaimError) {
            let title = t("error.linkError");
            let subTitle: string | null = null;
            let message = t("error.tryAgainLater");
            let supportEmail: string | undefined;
            let supportWhatsappNumber: string | undefined;

            switch (error.code) {
              case LINK_ERROR_CODE.LINK_NOT_FOUND:
              case LINK_ERROR_CODE.LINK_EXPIRED:
                title = t("error.linkExpired");
                message = t("error.linkExpiredMessage");
                break;
              case LINK_ERROR_CODE.LINK_REVOKED:
                title = t("error.linkRevoked");
                message = t("error.linkRevokedMessage");
                break;
              case LINK_ERROR_CODE.LINK_ALREADY_USED:
                title = t("error.linkAlreadyUsed");
                message = t("error.linkAlreadyUsedMessage");
                break;
              case LINK_ERROR_CODE.INVALID_TOKEN:
                title = t("error.invalidLink");
                message = t("error.invalidTokenMessage");
                break;
              case LINK_ERROR_CODE.RATE_LIMITED:
                title = t("error.rateLimitTitle");
                subTitle = t("error.rateLimitSubtitle");
                message = t("error.rateLimitMessage");
                break;
              case LINK_ERROR_CODE.SERVICE_UNAVAILABLE:
                title = t("error.serviceUnavailableTitle");
                message = t("error.serviceUnavailableMessage");
                break;
              case LINK_ERROR_CODE.SERVICE_UNAVAILABLE_MAINTENANCE: {
                const details = error.details as
                  | {
                      title?: string;
                      body?: string;
                      supportEmail?: string;
                      supportWhatsappNumber?: string;
                    }
                  | undefined;
                title = details?.title || t("error.maintenanceTitle");
                message = details?.body || t("error.maintenanceMessage");
                supportEmail = details?.supportEmail;
                supportWhatsappNumber = details?.supportWhatsappNumber;
                break;
              }
            }

            setError({
              title,
              subTitle,
              message,
              btnText: null,
              isOut: false,
              errorCode: error.code,
              supportEmail,
              supportWhatsappNumber,
            });
            if (
              error.code === LINK_ERROR_CODE.LINK_NOT_FOUND ||
              error.code === LINK_ERROR_CODE.LINK_EXPIRED ||
              error.code === LINK_ERROR_CODE.LINK_REVOKED
            ) {
              dispatchSessionExpiredEvent({
                code: error.code,
                details: error.details,
              });
            }
            if (error.code === LINK_ERROR_CODE.LINK_ALREADY_USED) {
              window.dispatchEvent(
                new CustomEvent(LINK_ALREADY_USED_EVENT, {
                  detail: {
                    code: error.code,
                    details: error.details,
                  },
                }),
              );
            }
            safeCapture(getEvents().LINK_OPENED, {
              success: false,
              error_code: error.code,
              link_id: linkId,
            });
          } else {
            setError({
              title: t("error.unexpectedError"),
              subTitle: null,
              message: t("error.tryAgainLater"),
              btnText: null,
              isOut: false,
              errorCode: LINK_ERROR_CODE.INTERNAL_ERROR,
            });
            safeCapture(getEvents().LINK_OPENED, {
              success: false,
              error_code: LINK_ERROR_CODE.INTERNAL_ERROR,
              link_id: linkId,
            });
          }
        }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    linkId,
    claimAttempt,
    revalidate,
    session.status,
    session.data?.linkId,
    setSession,
    setError,
    resetSession,
    setLinkId,
    setPartnerInfo,
    t,
    i18n,
  ]);
}
